/**
 * Task queue definitions — opinionated lanes for the operating console.
 *
 * Each queue answers a specific operational question:
 *   - Today        → "what's on my plate? Open + In Progress, sorted by recency."
 *   - Overdue      → "what's blowing past its date?"
 *   - Needs Triage → "what just came in and needs sorting?"
 *   - In Progress  → "what's actively in motion?"
 *   - Waiting on Client → "what's not on me?"
 *   - Ready for Review → "what's awaiting QA / sign-off?"
 *   - Needs Client Update → "where am I going dark on a client?"
 *   - Ready to Bill → "where's the unbilled revenue?"
 *   - Internal     → "non-client work / One Up work"
 *   - Done This Week → "what closed recently?"
 *   - All          → "everything"
 */
import { isOverdue, isThisWeek, isOlderThanDays } from './dates.js';
import type { Task } from './types.js';

export type TaskQueue =
  | 'today'
  | 'overdue'
  | 'needs_triage'
  | 'in_progress'
  | 'waiting_on_client'
  | 'review'
  | 'needs_client_update'
  | 'ready_to_bill'
  | 'internal'
  | 'done_this_week'
  | 'all';

export interface TaskQueueDescriptor {
  id: TaskQueue;
  label: string;
  description: string;
  /** Whether this queue should show in the default tab strip. */
  defaultVisible: boolean;
}

export const TASK_QUEUE_DESCRIPTORS: TaskQueueDescriptor[] = [
  { id: 'today', label: 'Today', description: 'Open and In Progress tasks, most recently modified first.', defaultVisible: true },
  { id: 'overdue', label: 'Overdue', description: 'Past target date and not closed.', defaultVisible: true },
  { id: 'needs_triage', label: 'Needs Triage', description: 'New, missing fields, or unsorted.', defaultVisible: true },
  { id: 'in_progress', label: 'In Progress', description: 'Actively being worked on.', defaultVisible: true },
  { id: 'waiting_on_client', label: 'Waiting on Client', description: 'Blocked on the client.', defaultVisible: true },
  { id: 'review', label: 'Ready for Review', description: 'Awaiting review, QA, or sign-off.', defaultVisible: true },
  { id: 'needs_client_update', label: 'Needs Client Update', description: 'Active but the client is going dark.', defaultVisible: true },
  { id: 'ready_to_bill', label: 'Ready to Bill', description: 'Billable work with tracked time, not yet billed.', defaultVisible: true },
  { id: 'internal', label: 'Internal', description: 'Non-client work or One Up internal.', defaultVisible: true },
  { id: 'done_this_week', label: 'Done This Week', description: 'Closed in the last 7 days.', defaultVisible: true },
  { id: 'all', label: 'All', description: 'Every task.', defaultVisible: true },
];

export interface MatchesQueueOptions {
  now?: Date;
  /** Days without a client update before needs_client_update fires. */
  needsUpdateAfterDays?: number;
}

export function matchesQueue(
  queue: TaskQueue,
  task: Task,
  options: MatchesQueueOptions = {},
): boolean {
  const { now = new Date(), needsUpdateAfterDays = 7 } = options;
  const isClosed = task.semanticStatus === 'done';

  switch (queue) {
    case 'all':
      return true;

    case 'today': {
      // Today = Open + In Progress only. Sorting by last-modified happens
      // in the view, not here — `matchesQueue` is filter-only.
      if (isClosed) return false;
      return task.semanticStatus === 'open' || task.semanticStatus === 'in_progress';
    }

    case 'overdue':
      return !isClosed && isOverdue(task.targetDate, now);

    case 'needs_triage': {
      if (isClosed) return false;
      if (task.semanticStatus === 'new' || task.semanticStatus === 'open') return true;
      if (!task.status) return true;
      if (task.flags.includes('no_client')) return true;
      if (task.flags.includes('no_next_action')) return true;
      return false;
    }

    case 'in_progress':
      return task.semanticStatus === 'in_progress';

    case 'waiting_on_client':
      return task.semanticStatus === 'waiting_on_client';

    case 'review':
      return (
        task.semanticStatus === 'review' ||
        (task.finalReview ?? '').toLowerCase().includes('review') &&
          !(task.finalReview ?? '').toLowerCase().includes('not reviewed')
      );

    case 'needs_client_update': {
      if (isClosed) return false;
      if (task.semanticStatus === 'new') return false;
      if (task.client?.isInternal) return false;
      // No client update field yet — fall back to stale status as a proxy.
      return isOlderThanDays(task.statusLastModified, needsUpdateAfterDays, now);
    }

    case 'ready_to_bill':
      return task.flags.includes('ready_to_bill');

    case 'internal': {
      if (task.client?.isInternal) return true;
      if (!task.client?.id && !task.client?.name) return true;
      if (task.semanticBillStatus === 'not_billable') return true;
      return false;
    }

    case 'done_this_week':
      return isClosed && isThisWeek(task.statusLastModified, now);

    default:
      return false;
  }
}

/** Compute counts for every queue in a single pass over the task list. */
export function computeQueueCounts(
  tasks: Task[],
  options: MatchesQueueOptions = {},
): Record<TaskQueue, number> {
  const counts = {
    today: 0,
    overdue: 0,
    needs_triage: 0,
    in_progress: 0,
    waiting_on_client: 0,
    review: 0,
    needs_client_update: 0,
    ready_to_bill: 0,
    internal: 0,
    done_this_week: 0,
    all: 0,
  } as Record<TaskQueue, number>;

  for (const task of tasks) {
    counts.all += 1;
    for (const descriptor of TASK_QUEUE_DESCRIPTORS) {
      if (descriptor.id === 'all') continue;
      if (matchesQueue(descriptor.id, task, options)) {
        counts[descriptor.id] += 1;
      }
    }
  }

  return counts;
}
